// Remembers each asset's chart selection (forecast + variation) in the browser, so swiping between assets
// brings back what the user last looked at on each. Local only — nothing is sent to the backend.
// localStorage rather than a cookie: cookies ride along on every request and cap at ~4KB, and this never needs the server.

const STORAGE_KEY = "eai.assetGraphSelection.v1";

export interface AssetGraphSelection {
  forecast: string;
  variation: string;
  /** Further forecasts shown stacked under the main one */
  extras?: string[];
  /** The user's order for every channel in the picker; the stacked charts follow it */
  order?: string[];
}

const readAll = (): Record<string, AssetGraphSelection> => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const write = (value: Record<string, AssetGraphSelection>) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage blocked or full — the selection just won't persist
  }
};

export const loadAssetGraphSelection = (assetId?: string | null): AssetGraphSelection | null => {
  if (!assetId) return null;
  const saved = readAll()[assetId];
  if (!saved || typeof saved.forecast !== "string" || typeof saved.variation !== "string") return null;
  return {
    ...saved,
    extras: Array.isArray(saved.extras) ? saved.extras.filter((k) => typeof k === "string") : [],
    order: Array.isArray(saved.order) ? saved.order.filter((k) => typeof k === "string") : [],
  };
};

export const saveAssetGraphSelection = (
  assetId: string | null | undefined,
  forecast: string | null | undefined,
  variation: string | null | undefined
): void => {
  if (!assetId || !forecast || !variation) return;
  const all = readAll();
  write({ ...all, [assetId]: { ...all[assetId], forecast, variation } });
};

/** Remembers the stacked extras without disturbing the main forecast's own record. */
export const saveAssetGraphExtras = (assetId: string | null | undefined, extras: string[]): void => {
  if (!assetId) return;
  const all = readAll();
  const current = all[assetId];
  if (!current) return;
  write({ ...all, [assetId]: { ...current, extras } });
};

/** Remembers the channel order the picker was left in. */
export const saveAssetGraphOrder = (assetId: string | null | undefined, order: string[]): void => {
  if (!assetId) return;
  const all = readAll();
  const current = all[assetId];
  if (!current) return;
  write({ ...all, [assetId]: { ...current, order } });
};
